import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, Menu, Lock, LogOut, ShoppingBag, ExternalLink, FileText, Search, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { detectRegion, getSplitFlagPair } from '../utils/regionDetect';
import LoginModal from './LoginModal';

const PRICING_PATHS = ['/products', '/order', '/order/kei-truck'];

/* Split-flag icon: two flag images side by side, clipped to create a single split element */
const SplitFlag = ({ size = 16 }) => {
  const region = useMemo(() => detectRegion(), []);
  const flags = useMemo(() => getSplitFlagPair(region), [region]);
  return (
    <div style={{ width: size, height: size, borderRadius: 3, overflow: 'hidden', display: 'flex', flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }}>
      <img src={flags.left} alt="" style={{ width: '50%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
      <img src={flags.right} alt="" style={{ width: '50%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
    </div>
  );
};

const getNavLinks = (t) => [
  {
    label: t('nav.products'),
    href: '/products',
    sub: [
      { label: t('nav.truckCampers'), desc: t('nav.truckCampersDesc'), href: '/products?tab=pre-assembled', badge: t('nav.new2026') },
      { label: t('nav.keiTruck'), desc: t('nav.keiTruckDesc'), href: '/products?tab=kei-truck', badge: t('nav.new2026') },
      { label: t('nav.flatKits'), desc: t('nav.flatKitsDesc'), href: '/products?tab=expedition-kits' },
      { label: t('nav.truckBodies'), desc: t('nav.truckBodiesDesc'), href: '/products?tab=truck-bodies' },
      { label: t('nav.customProjects'), desc: t('nav.customProjectsDesc'), href: '/products?tab=custom' },
    ],
  },
  { label: t('nav.whyTC'), href: '/why-total-composites' },
  {
    label: t('nav.newsMedia'),
    href: '/expedition',
    sub: [
      { label: t('nav.expeditions'), desc: t('nav.expeditionsDesc'), href: '/expedition' },
      { label: t('nav.articles'), desc: t('nav.articlesDesc'), href: '/articles' },
      { label: t('nav.media'), desc: t('nav.mediaDesc'), href: '/media' },
      { label: t('nav.gallery'), desc: t('nav.galleryDesc'), href: '/gallery' },
    ],
  },
  { label: t('nav.builderResources'), href: '/builder-resources' },
  { label: t('nav.partners'), href: '/partners' },
  { label: t('nav.about'), href: '/about' },
  { label: t('nav.contact'), href: '/contact' },
];

/* ── Animated topographic contour canvas (white lines) ── */
const MountainCanvas = ({ visible }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      const t = timeRef.current;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      const numLines = 90;
      for (let i = 0; i < numLines; i++) {
        const baseY = (i / numLines) * H;
        ctx.beginPath();
        for (let x = 0; x <= W * 0.72; x += 2) {
          const nx = x / W;
          const y = baseY
            + Math.sin(nx * 6.2  + i * 0.38 + t * 0.18) * 22
            + Math.sin(nx * 14   + i * 0.22 - t * 0.11) * 10
            + Math.sin(nx * 3.1  - i * 0.15 + t * 0.07) * 32
            + Math.sin(nx * 22   + i * 0.5  + t * 0.25) * 5
            + Math.sin(nx * 1.8  + i * 0.08 - t * 0.04) * 18;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        const brightness = 0.25 + 0.55 * Math.abs(Math.sin(i * 0.19 + t * 0.05));
        ctx.strokeStyle = `rgba(255,255,255,${brightness})`;
        ctx.lineWidth = 0.9;
        ctx.stroke();
      }

      const grad = ctx.createLinearGradient(W * 0.38, 0, W * 0.72, 0);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,1)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#000';
      ctx.fillRect(W * 0.72, 0, W * 0.28, H);

      timeRef.current += 0.012;
      animRef.current = requestAnimationFrame(draw);
    };

    if (visible) { timeRef.current = 0; draw(); }
    else if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }

    return () => { window.removeEventListener('resize', resize); if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [visible]);

  return (
    <canvas ref={canvasRef} className="absolute inset-0 w-full h-full"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.5s ease' }} />
  );
};

/* ── Online Shop Button — Expedition Upfitter ── */
const EUF_COLOR = '#7796a8';

const OnlineShopButton = ({ hovered, onEnter, onLeave, label }) => (
  <a
    href="https://www.expeditionupfitter.com"
    target="_blank"
    rel="noopener noreferrer"
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest py-3 transition-all duration-200"
    style={{
      width: 220,
      justifyContent: 'center',
      border: `1px solid ${EUF_COLOR}`,
      color: '#fff',
      backgroundColor: hovered ? '#5a7d94' : EUF_COLOR,
      boxShadow: hovered ? `0 0 18px ${EUF_COLOR}55` : 'none',
      textDecoration: 'none',
    }}
  >
    <ShoppingBag size={12} />
    {label}
    <ExternalLink size={9} style={{ opacity: 0.6 }} />
  </a>
);

/* ── Main Navbar ── */
const Navbar = ({ loaderDone }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [shopHovered, setShopHovered] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const { language, switchLanguage, t } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const NAV_LINKS = getNavLinks(t);
  const showCurrencyToggle = PRICING_PATHS.some(p => location.pathname.startsWith(p));
  const searchInputRef = React.useRef(null);
  const [siteContent, setSiteContent] = useState([]);

  // Comprehensive static content index — covers ALL text on every page
  const STATIC_PAGES = React.useMemo(() => [
    // Products
    { label: t('nav.products'), href: '/products', type: 'page', keywords: 'products campers kits truck bodies produits most advanced truck camper shell composite panels insulated lightweight 4-season overlanding overland expedition slide-in campeurs portés le plus avancé panneaux composites' },
    { label: t('nav.truckCampers'), href: '/products?tab=pre-assembled', type: 'page', keywords: '2026 truck campers pre-assembled lynx wolf wolverine campeurs portés toyota tacoma jeep gladiator F-150 F-350 flatbed camper shell lightweight departure angle off-road mid-size full-size slide-in composite 4-season' },
    { label: "LYNX 7'", href: '/products?tab=pre-assembled', type: 'product', keywords: 'lynx 7 tacoma gladiator mid-size truck camper lightweight composite slide-in' },
    { label: "WOLF 6.5' / WOLF 8'", href: '/products?tab=pre-assembled', type: 'product', keywords: 'wolf 6.5 wolf 8 F-150 full-size truck camper composite slide-in' },
    { label: "WOLVERINE 6.5' / WOLVERINE 8'", href: '/products?tab=pre-assembled', type: 'product', keywords: 'wolverine 6.5 wolverine 8 flatbed F-350 departure angle off-road expedition overland' },
    { label: t('nav.keiTruck'), href: '/products?tab=kei-truck', type: 'page', keywords: 'kei truck box caisson micro ultra-light insulated composite cargo box purpose-built mobile food reefer unit refrigeration temperature controlled micro camper conversion expedition cargo system sleeping platform off-grid minimalist overland rigs camion kei' },
    { label: t('nav.flatKits'), href: '/products?tab=expedition-kits', type: 'page', keywords: 'expedition kits panels assembly ready-to-assemble composite panel systems professional builders dealer wholesale programs custom dimensions pre-cut labeled welding fabrication kits expédition panneaux constructeurs professionnels revendeurs dimensions personnalisées' },
    { label: t('nav.truckBodies'), href: '/products?tab=truck-bodies', type: 'page', keywords: 'truck bodies commercial carrosseries refrigerated delivery cold chain logistics food service pharmaceutical dry freight utility bodies service vehicles tradespeople delivery operations north america livraison réfrigérée fret sec utilitaires' },
    { label: t('nav.customProjects'), href: '/products?tab=custom', type: 'page', keywords: 'custom projects bespoke expedition build one-off design engineering sur mesure projets personnalisés expédition construction unique ingénierie conception' },

    // Why TC
    { label: t('nav.whyTC'), href: '/why-total-composites', type: 'page', keywords: 'why total composites composite comparison insulation thermal pourquoi R-value FRP PET foam core vacuum bonding fiberglass aluminum steel weight condensation moisture rot thermal bridging cold bridging zero thermal conductivity durability lightweight strong sandwich panel construction technology manufacturing process' },

    // Expeditions — FULL content keywords
    { label: t('nav.expeditions'), href: '/expedition', type: 'page', keywords: 'expeditions adventure travel expéditions aventure arctic trucks magnetic north pole yellowknife NWT transglobal car expedition circumnavigate earth poles south north pole andreas schwall esquimalt victoria BC habitat design extreme conditions -39°C 475 days arctic ocean antarctic plateau truck camper adventure AT44 ford F-350 6x6 conversion real expedition real conditions condensation ice build-up superior insulation design quality durability thermal-free composite construction traditional builds fail engineering reality january 2024 april 2023 april 2025' },
    { label: 'Arctic Trucks — Magnetic North Pole', href: '/expedition', type: 'expedition', keywords: 'arctic trucks magnetic north pole yellowknife NWT expedition 2023 april -39 celsius habitat shelter bobcat 8 AT44 F-350 6x6 director sales 24 days 7 team members real conditions' },
    { label: 'Transglobal Car Expedition', href: '/expedition', type: 'expedition', keywords: 'transglobal car expedition circumnavigate earth poles january 2024 april 2025 475 days arctic ocean antarctic plateau andreas schwall esquimalt total composites habitat design transglobalcar.com world route' },

    // Other pages
    { label: t('nav.builderResources'), href: '/builder-resources', type: 'page', keywords: 'builder resources guides manuals assembly guides video training technical documentation installation instructions ressources constructeurs guides assemblage formation vidéo' },
    { label: t('nav.partners'), href: '/partners', type: 'page', keywords: 'partners dealers distributors partenaires NORDVAN TANK EXPEDITION UNIMOG CANADA PRITSCHENBUDE TURAS ADVENTURE VEHICLE AeonRV authorized dealer network fabrication shops volume pricing' },
    { label: t('nav.gallery'), href: '/gallery', type: 'page', keywords: 'gallery photos images galerie adventure builds customer vehicles truck camper overland expedition installations builds' },
    { label: t('nav.articles'), href: '/articles', type: 'page', keywords: 'blog articles news blogue nouvelles truck camper magazine industry press updates' },
    { label: t('nav.media'), href: '/media', type: 'page', keywords: 'media press coverage médias presse truck camper magazine overland expo transglobal expedition arctic trucks features reviews publications interviews' },
    { label: t('nav.about'), href: '/about', type: 'page', keywords: 'about us company victoria bc à propos entreprise andreas schwall founder german expat british columbia canada 2001 2015 composite panels german standards german components china factory plastics engineer personal obsession RV overland build history origin story esquimalt' },
    { label: t('nav.contact'), href: '/contact', type: 'page', keywords: 'contact email phone quote devis téléphone courriel info@totalcomposites.com 250 477 0375 victoria bc canada get in touch reach us inquiry request information' },
    { label: language === 'fr' ? 'Commander' : 'Order', href: '/order', type: 'page', keywords: 'order form buy purchase commander acheter formulaire deposit non-refundable prices CAD crew cab extended cab flatbed long bed short bed specifications options pricing taxes freight shipping production' },
    { label: 'FAQ', href: '/why-total-composites#faq', type: 'page', keywords: 'faq questions answers frequently asked help aide réponses' },
  ], [t, language]);

  const COMMONLY_SEARCHED = React.useMemo(() => [
    { label: t('nav.products'), href: '/products' },
    { label: t('nav.whyTC'), href: '/why-total-composites' },
    { label: 'FAQ', href: '/why-total-composites#faq' },
    { label: t('nav.gallery'), href: '/gallery' },
    { label: t('nav.contact'), href: '/contact' },
    { label: language === 'fr' ? 'Commander' : 'Order', href: '/order' },
  ], [t, language]);

  // Fetch FAQ + articles for live search
  React.useEffect(() => {
    const fetchContent = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const [faqRes, articlesRes] = await Promise.all([
          fetch(`${API}/api/faq`),
          fetch(`${API}/api/articles`).catch(() => null),
        ]);
        const content = [];
        // FAQ items
        if (faqRes.ok) {
          const data = await faqRes.json();
          data.forEach(cat => {
            (cat.items || []).forEach(item => {
              content.push({
                label: language === 'fr' ? (item.question_fr || item.question_en) : item.question_en,
                answer: language === 'fr' ? (item.answer_fr || item.answer_en) : item.answer_en,
                href: '/why-total-composites#faq',
                type: 'faq',
                keywords: `${item.question_en} ${item.question_fr || ''} ${item.answer_en} ${item.answer_fr || ''}`,
              });
            });
          });
        }
        // Articles
        if (articlesRes && articlesRes.ok) {
          const articles = await articlesRes.json();
          (articles || []).forEach(article => {
            content.push({
              label: article.title || 'Article',
              answer: (article.excerpt || article.content || '').substring(0, 120),
              href: `/articles/${article.slug || article.id}`,
              type: 'article',
              keywords: `${article.title || ''} ${article.excerpt || ''} ${article.content || ''}`.substring(0, 500),
            });
          });
        }
        setSiteContent(content);
      } catch { /* silent */ }
    };
    fetchContent();
  }, [language]);

  const searchResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const pageResults = STATIC_PAGES.filter(p =>
      p.label.toLowerCase().includes(q) || p.keywords.toLowerCase().includes(q)
    ).slice(0, 4);
    const contentResults = siteContent.filter(f =>
      f.label.toLowerCase().includes(q) || f.keywords.toLowerCase().includes(q)
    ).slice(0, 4);
    return [...pageResults, ...contentResults].slice(0, 8);
  }, [searchQuery, STATIC_PAGES, siteContent]);

  // Extract a contextual snippet with the matched word, returns { before, match, after }
  const getSnippet = React.useCallback((item, query) => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    // Search in: answer first (richer text), then keywords, then label
    const sources = [item.answer, item.keywords, item.label].filter(Boolean);
    for (const src of sources) {
      const idx = src.toLowerCase().indexOf(q);
      if (idx === -1) continue;
      const start = Math.max(0, idx - 40);
      const end = Math.min(src.length, idx + q.length + 60);
      const before = (start > 0 ? '...' : '') + src.substring(start, idx);
      const match = src.substring(idx, idx + q.length);
      const after = src.substring(idx + q.length, end) + (end < src.length ? '...' : '');
      return { before, match, after };
    }
    return null;
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setSubOpen(null); setSearchOpen(false); setSearchQuery(''); }, [location]);

  useEffect(() => {
    document.body.style.overflow = (menuOpen || searchOpen) ? 'hidden' : '';
    if (!menuOpen) setSubOpen(null);
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, searchOpen]);

  // Auto-focus search input when overlay opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [searchOpen]);

  return (
    <>
      {/* Nav bar — hides when menu opens */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 sm:px-6 transition-all duration-400"
        style={{
          height: 76,
          backgroundColor: scrolled ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.82)',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transform: menuOpen ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.35s cubic-bezier(0.76,0,0.24,1), background-color 0.5s ease, backdrop-filter 0.5s ease',
        }}
      >
        {/* Left spacer — smaller on mobile */}
        <div className="w-0 sm:w-32" />

        {/* Center: Logo — smaller on mobile to avoid overlapping right icons */}
        <Link to="/" className="absolute left-1/2 -translate-x-1/2"
          style={{ opacity: loaderDone ? 1 : 0, transition: 'opacity 0.5s ease 0.3s' }}>
          <img src="/tc_logo.png" alt="Total Composites" className="w-auto object-contain h-9 sm:h-[52px]"
            style={{ filter: 'invert(1)' }} />
        </Link>

        {/* Right: Search + Language Toggle (desktop only) + Hamburger */}
        <div className="flex justify-end items-center gap-1.5 sm:gap-3" style={{ minWidth: 'fit-content' }}>
          <button
            onClick={() => { setSearchOpen(true); setMenuOpen(false); }}
            className="flex items-center justify-center text-white hover:opacity-70 transition-opacity"
            style={{ padding: '8px', minHeight: 44, minWidth: 44 }}
            data-testid="search-icon-btn"
          >
            <Search size={18} />
          </button>
          <button
            onClick={() => switchLanguage(language === 'en' ? 'fr' : 'en')}
            className="hidden sm:flex items-center text-white hover:opacity-85 transition-opacity"
            style={{ minHeight: 36, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6 }}
            data-testid="lang-toggle"
          >
            <span style={{ padding: '6px 12px', color: language === 'en' ? '#E8520A' : 'rgba(255,255,255,0.5)', transition: 'color 0.2s', lineHeight: 1, display: 'flex', alignItems: 'center' }}>ENG</span>
            <span style={{ width: 1, height: 14, backgroundColor: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
            <span style={{ padding: '6px 12px', color: language === 'fr' ? '#E8520A' : 'rgba(255,255,255,0.5)', transition: 'color 0.2s', lineHeight: 1, display: 'flex', alignItems: 'center' }}>FR</span>
          </button>
          {showCurrencyToggle && (
            <button
              onClick={() => setCurrency(currency === 'CAD' ? 'USD' : 'CAD')}
              className="hidden sm:flex items-center gap-1.5 text-white hover:opacity-70 transition-opacity"
              style={{ padding: '6px 10px', minHeight: 36, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6 }}
              data-testid="currency-toggle"
            >
              <DollarSign size={12} />
              {currency === 'CAD' ? 'USD' : 'CAD'}
            </button>
          )}
          <button onClick={() => setMenuOpen(o => !o)} className="text-white hover:opacity-70 transition-opacity" style={{ padding: '10px 8px', minHeight: 44, display: 'flex', alignItems: 'center' }} data-testid="hamburger-menu-btn">
            <Menu size={20} />
          </button>
        </div>
      </nav>

      {/* Full-screen menu overlay */}
      <div
        className="fixed inset-0 z-50 overflow-hidden"
        style={{
          pointerEvents: menuOpen ? 'all' : 'none',
          opacity: menuOpen ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        {/* Animated contour canvas */}
        <MountainCanvas visible={menuOpen} />

        {/* Close button — top right */}
        <button
          onClick={() => setMenuOpen(false)}
          className="absolute top-6 right-6 z-10 text-white hover:opacity-70 transition-opacity" style={{ minHeight: "44px", minWidth: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <X size={20} />
        </button>

        {/* Logo — upper right, inside menu */}
        <Link
          to="/"
          className="absolute top-5 right-14 z-10"
          style={{
            opacity: menuOpen ? 1 : 0,
            transform: menuOpen ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.4s ease 0.15s, transform 0.4s ease 0.15s',
          }}
        >
          <img src="/tc_logo.png" alt="Total Composites" className="w-auto object-contain"
            style={{ filter: 'invert(1)', height: 52 }} />
        </Link>

        {/* Menu links — upper right */}
        <div className="absolute top-20 right-8 sm:right-14 flex flex-col items-end pt-4">
          <nav className="text-right">
            {NAV_LINKS.map((link, i) => (
              <div key={link.href + link.label}>
                {link.sub ? (
                  /* Submenu — hover reveals sub-items inline */
                  <div
                    onMouseEnter={() => setSubOpen(link.label)}
                    onMouseLeave={() => setSubOpen(null)}
                  >
                    {/* Parent label */}
                    <span
                      className="block font-black uppercase tracking-wide cursor-default py-1 transition-all duration-200"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.95rem, 1.8vw, 1.35rem)',
                        color: subOpen === link.label ? '#E8520A' : '#fff',
                        opacity: menuOpen ? 1 : 0,
                        transform: menuOpen ? 'translateX(0)' : 'translateX(25px)',
                        transition: `opacity 0.4s ease ${i * 35 + 80}ms, transform 0.35s ease ${i * 35 + 80}ms, color 0.2s ease`,
                      }}
                    >
                      {link.label}
                    </span>

                    {/* Sub-items — expand below */}
                    <div
                      style={{
                        overflow: 'hidden',
                        maxHeight: subOpen === link.label ? '400px' : '0px',
                        opacity: subOpen === link.label ? 1 : 0,
                        transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease',
                      }}
                    >
                      <div className="py-2 space-y-0 border-r pr-0" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        {link.sub.map((item, j) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="block py-1.5 group text-right"
                            style={{
                              opacity: subOpen === link.label ? 1 : 0,
                              transform: subOpen === link.label ? 'translateX(0)' : 'translateX(15px)',
                              transition: `opacity 0.3s ease ${j * 35 + 60}ms, transform 0.3s ease ${j * 35 + 60}ms`,
                            }}
                          >
                            <div className="flex items-center justify-end gap-2">
                              {item.badge && (
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#E8520A', color: '#fff', fontSize: '0.55rem' }}>
                                  {item.badge}
                                </span>
                              )}
                              <span className="font-bold uppercase tracking-wide text-gray-400 group-hover:text-white transition-colors"
                                style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(0.72rem, 1.2vw, 0.9rem)' }}>
                                {item.label}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.href}
                    className="block font-black uppercase tracking-wide text-white hover:text-orange-500 transition-all duration-200 py-1"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.95rem, 1.8vw, 1.35rem)',
                      opacity: menuOpen ? 1 : 0,
                      transform: menuOpen ? 'translateX(0)' : 'translateX(25px)',
                      transition: `opacity 0.4s ease ${i * 35 + 80}ms, transform 0.35s ease ${i * 35 + 80}ms, color 0.2s ease`,
                    }}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Buttons — all equal width, flat icons */}
          {(() => {
            return (
              <div className="mt-6 flex flex-col items-end gap-3 relative" style={{ overflow: "visible" }}
                style={{
                  opacity: menuOpen ? 1 : 0,
                  transform: menuOpen ? 'translateX(0)' : 'translateX(20px)',
                  transition: `opacity 0.4s ease ${NAV_LINKS.length * 35 + 120}ms, transform 0.4s ease ${NAV_LINKS.length * 35 + 120}ms`,
                }}>

                {/* Popup — full height of all 3 boxes, left of container */}
                <div
                  style={{
                    position: 'absolute',
                    right: 'calc(100% + 12px)', overflow: 'hidden',
                    top: 0,
                    bottom: 0,
                    width: 380,
                    backgroundColor: '#000',
                    border: `1px solid ${EUF_COLOR}`,
                    borderRadius: 6,
                    display: 'flex',
                    flexDirection: 'row',
                    overflow: 'hidden',
                    opacity: shopHovered ? 1 : 0,
                    transform: shopHovered ? 'scale(1)' : 'scale(0.97)',
                    transformOrigin: 'right center',
                    transition: 'opacity 0.25s ease, transform 0.25s ease',
                    pointerEvents: 'none',
                    boxShadow: `0 8px 32px rgba(0,0,0,0.7)`,
                    zIndex: 10,
                  }}
                >
                  {/* Logo — left column, centered */}
                  <div style={{ width: 130, flexShrink: 0, backgroundColor: '#0a1520', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 14px' }}>
                    <img
                      src="https://expeditionupfitter.com/cdn/shop/files/Expeditionupfitter-logo_1_400aa74f-92b0-4955-9222-708e18a88ae2_271x250.png?v=1732128028"
                      alt="Expedition Upfitter"
                      style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  {/* Text — right column */}
                  <div style={{ flex: 1, padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5, minWidth: 0, overflow: 'hidden' }}>
                    <p style={{ color: EUF_COLOR, fontFamily: 'Inter,sans-serif', fontSize: '0.82rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0, whiteSpace: 'nowrap' }}>
                      Expedition Upfitter
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', margin: 0, whiteSpace: 'nowrap' }}>
                      {t('navPopup.eufSubtitle')}
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.76rem', lineHeight: 1.45, margin: 0 }}>
                      {t('navPopup.eufDescription')}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'rgba(255,255,255,0.25)', marginTop: 2 }}>
                      <ExternalLink size={8} />
                      <span style={{ fontSize: '0.68rem' }}>expeditionupfitter.com</span>
                    </div>
                  </div>
                  {/* Arrow → right */}
                  <div style={{ position: 'absolute', right: -6, top: '50%', width: 10, height: 10, backgroundColor: '#000', border: `1px solid ${EUF_COLOR}`, borderTop: 'none', borderLeft: 'none', transform: 'translateY(-50%) rotate(-45deg)' }} />
                </div>

                {/* GET A QUOTE */}
                <Link to="/contact"
                  className="inline-flex items-center gap-2.5 border border-white text-white text-xs font-bold uppercase tracking-widest py-3 hover:bg-white hover:text-black transition-all duration-200"
                  style={{ width: 220, justifyContent: 'center' }}>
                  <FileText size={12} /> {language === 'fr' ? 'Demander un devis' : 'Get a Quote'}
                </Link>

                {/* ONLINE SHOP */}
                <OnlineShopButton hovered={shopHovered} onEnter={() => setShopHovered(true)} onLeave={() => setShopHovered(false)} label={t('nav.onlineShop')} />

                {/* PARTNER PORTAL */}
                {user ? (
                  <div className="flex items-center gap-2" style={{ width: 220 }}>
                    <button onClick={() => navigate(user.role === 'admin' ? '/portal/admin' : '/portal')}
                      className="inline-flex items-center gap-2.5 border border-white text-white text-xs font-bold uppercase tracking-widest py-3 hover:bg-white hover:text-black transition-all duration-200 flex-1"
                      style={{ justifyContent: 'center' }}>
                      <Lock size={12} /> {t('nav.portal')}
                    </button>
                    <button onClick={() => logout()} className="text-gray-500 hover:text-white transition-colors px-2" title={t('nav.logOut')}>
                      <LogOut size={13} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => { setMenuOpen(false); setShowLogin(true); }}
                    className="inline-flex items-center gap-2.5 border border-white text-white text-xs font-bold uppercase tracking-widest py-3 hover:bg-white hover:text-black transition-all duration-200"
                    style={{ width: 220, justifyContent: 'center' }}>
                    <Lock size={12} /> {t('nav.logIn')}
                  </button>
                )}

                {/* Language Toggle — mobile only, inside menu */}
                <button
                  onClick={() => { switchLanguage(language === 'en' ? 'fr' : 'en'); setMenuOpen(false); }}
                  className="sm:hidden inline-flex items-center justify-center border text-xs font-bold uppercase tracking-widest hover:opacity-85 transition-all duration-200"
                  style={{ width: 160, borderColor: 'rgba(255,255,255,0.3)', borderRadius: 6 }}
                  data-testid="lang-toggle-mobile"
                >
                  <span style={{ padding: '12px 18px', color: language === 'en' ? '#E8520A' : 'rgba(255,255,255,0.6)', transition: 'color 0.2s', lineHeight: 1, display: 'flex', alignItems: 'center' }}>ENG</span>
                  <span style={{ width: 1, height: 16, backgroundColor: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                  <span style={{ padding: '12px 18px', color: language === 'fr' ? '#E8520A' : 'rgba(255,255,255,0.6)', transition: 'color 0.2s', lineHeight: 1, display: 'flex', alignItems: 'center' }}>FR</span>
                </button>

                {/* Currency Toggle — mobile only, inside menu, only on pricing pages */}
                {showCurrencyToggle && (
                  <button
                    onClick={() => { setCurrency(currency === 'CAD' ? 'USD' : 'CAD'); setMenuOpen(false); }}
                    className="sm:hidden inline-flex items-center gap-2.5 border text-white text-xs font-bold uppercase tracking-widest py-3 hover:bg-white hover:text-black transition-all duration-200"
                    style={{ width: 220, justifyContent: 'center', borderColor: 'rgba(255,255,255,0.3)' }}
                    data-testid="currency-toggle-mobile"
                  >
                    <DollarSign size={12} />
                    {currency === 'CAD' ? 'Switch to USD' : 'Switch to CAD'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Full-screen Search Overlay (Flated-style) ── */}
      <div
        className="fixed inset-0 z-[9999]"
        style={{
          pointerEvents: searchOpen ? 'all' : 'none',
          opacity: searchOpen ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
        data-testid="search-overlay"
      >
        {/* Backdrop — fully opaque black */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: '#000' }}
          onClick={() => setSearchOpen(false)}
        />

        {/* Scrollable content wrapper */}
        <div className="relative z-10 flex flex-col items-center h-full overflow-y-auto overscroll-contain"
          style={{ paddingTop: 'clamp(60px, 10vh, 112px)', paddingBottom: 40 }}
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          {/* Close */}
          <button
            onClick={() => setSearchOpen(false)}
            className="fixed top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-white transition-colors z-20"
            style={{ minHeight: 48, minWidth: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="search-close-btn"
          >
            <X size={24} />
          </button>

          {/* Title */}
          <h2
            className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] mb-6 sm:mb-8 px-4 text-center"
            style={{ color: 'rgba(255,255,255,0.35)' }}
          >
            {language === 'fr' ? 'Rechercher totalcomposites.com' : 'Search totalcomposites.com'}
          </h2>

          {/* Search Input — responsive max width and padding */}
          <div className="w-full px-4 sm:px-6" style={{ maxWidth: 680 }}>
            <div className="relative">
              <Search size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={language === 'fr' ? 'Que recherchez-vous ?' : 'What are you looking for?'}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-base sm:text-lg text-white placeholder-gray-600 focus:outline-none"
                style={{
                  backgroundColor: 'transparent',
                  borderBottom: '2px solid rgba(255,255,255,0.15)',
                  borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                  borderRadius: 0,
                }}
                data-testid="nav-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  style={{ minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  data-testid="search-clear-btn"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Live Results */}
            {searchQuery.trim() && searchResults.length > 0 && (
              <div className="mt-4 sm:mt-6 space-y-1" data-testid="nav-search-results">
                {searchResults.map((result, idx) => {
                  const snippet = getSnippet(result, searchQuery);
                  return (
                    <Link
                      key={`${result.href}-${result.label}-${idx}`}
                      to={result.href}
                      className="flex items-start gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-lg transition-colors hover:bg-white/5 group"
                      data-testid={`search-result-${idx}`}
                    >
                      {result.type === 'faq' ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border flex-shrink-0 mt-0.5" style={{ color: '#E8520A', borderColor: 'rgba(232,82,10,0.3)' }}>FAQ</span>
                      ) : result.type === 'product' ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border flex-shrink-0 mt-0.5" style={{ color: '#3B82F6', borderColor: 'rgba(59,130,246,0.3)' }}>Products</span>
                      ) : result.type === 'expedition' ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border flex-shrink-0 mt-0.5" style={{ color: '#10B981', borderColor: 'rgba(16,185,129,0.3)' }}>Expeditions</span>
                      ) : result.type === 'article' ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border flex-shrink-0 mt-0.5" style={{ color: '#8B5CF6', borderColor: 'rgba(139,92,246,0.3)' }}>Articles</span>
                      ) : (
                        <Search size={14} className="text-gray-600 group-hover:text-gray-400 flex-shrink-0 mt-1" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-300 group-hover:text-white transition-colors truncate">{result.label}</p>
                        {snippet && (
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            <span>{snippet.before}</span>
                            <span className="text-white font-bold">{snippet.match}</span>
                            <span>{snippet.after}</span>
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* No Results */}
            {searchQuery.trim() && searchResults.length === 0 && (
              <p className="mt-8 text-center text-sm text-gray-600">
                {language === 'fr' ? 'Aucun résultat trouvé' : 'No results found'}
              </p>
            )}

            {/* Commonly Searched — show when no query */}
            {!searchQuery.trim() && (
              <div className="mt-8 sm:mt-10">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] mb-3 sm:mb-4" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  {language === 'fr' ? 'Recherches populaires' : 'Commonly searched'}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="commonly-searched">
                  {COMMONLY_SEARCHED.map(item => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider text-gray-400 rounded-full transition-all hover:text-white hover:border-white"
                      style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </>
  );
};

export default Navbar;
